import React, { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../UserContext";
import Header from "../Header";
import apiClient from "../../api/client";
import SearchableSelect from "../SearchableSelect";
import { toast } from "react-toastify";
import { HiOutlineRefresh } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import "./index.css";

// type: "internet" (default) -> lists Internet customers
//       "dvpl"                -> lists DVPL customers
// Same list page, same columns/filters — just points internet_customer_list.php
// and the customer deep-link at the other provider.
const InternetCustomerList = ({ type = "dvpl" }) => {

    const { token, user } = useContext(UserContext);
    const role = user?.role;

    const providerLabel = type === "dvpl" ? "DVPL" : "Internet";
    const deepLinkParam = type === "dvpl" ? "dvpl" : "internet";

    const [customers, setCustomers] = useState([]);

    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");

    // Status filter is driven by the stat cards themselves — clicking
    // "Active" both shows the count AND filters to it, rather than
    // having a separate count display and a separate filter control
    // that could drift out of sync with each other.
    const [statusFilter, setStatusFilter] = useState("all");

    // Partner/branch filters — admin/superadmin only. For an operator,
    // the backend already restricts the list to their own single
    // branch, so a branch filter would only ever have one option and
    // isn't shown for that role.
    const [partnerFilter, setPartnerFilter] = useState("all");
    const [branchFilter, setBranchFilter] = useState("all");

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [internetInfo, setInternetInfo] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {

        loadCustomers();
 
    }, [type]);

    const loadCustomers = async () => {

        setLoading(true);

        try {

            const { data } = await apiClient.get(
                `/customer/internet_customer_list.php?type=${type}`
            );

            if (data.status === 200) {

                setCustomers(data.results || []);

            } else {

                toast.error(data.message || "Unable to load customers");

            }

        } catch (e) {

            toast.error("Network Error");

        }

        setLoading(false);

    };

    // Best-effort partner name lookup — I haven't seen a raw
    // /api/customers response to confirm the exact field name the
    // provider actually uses, only that partner_id shows up in the
    // add/verify payloads elsewhere. Tries a couple of likely names and
    // falls back to the numeric ID so the filter still works with
    // *something* identifying even if the exact key differs. Correct
    // this to the real field name once you check an actual response.
    const getPartnerLabel = (c) =>
        c.partner_name || c.partnerName || (c.partner_id != null ? `Partner #${c.partner_id}` : null);

    // ---- Stat counts — computed from the FULL list, not the filtered
    // one, so the cards always show the true totals regardless of
    // whatever filter/search is currently applied. ----
    const stats = useMemo(() => {
        const counts = { total: customers.length, active: 0, expired: 0, new: 0, online: 0, offline: 0, other: 0 };

        customers.forEach((c) => {
            if (c.status === "Active") {
                counts.active++;
                if (c.online_status === "ONLINE") {
                    counts.online++;
                } else {
                    counts.offline++;
                }
            } else if (c.status === "Expired") {
                counts.expired++;
            } else if (c.status === "New") {
                counts.new++;
            } else {
                // Catches any other status value the provider might
                // return that isn't one of the three above, so nothing
                // silently goes uncounted.
                counts.other++;
            }
        });

        return counts;
    }, [customers]);

    // ---- Unique partner/branch values present in the data, for the
    // filter dropdowns. ----
    const partnerOptions = useMemo(() => {
        const set = new Set();
        customers.forEach((c) => {
            const label = getPartnerLabel(c);
            if (label) set.add(label);
        });
        return Array.from(set).sort();
    }, [customers]);

    const branchOptions = useMemo(() => {
        const set = new Set();
        customers.forEach((c) => {
            if (c.branch_name) set.add(c.branch_name);
        });
        return Array.from(set).sort();
    }, [customers]);

    const toggleStatusFilter = (value) => {
        setStatusFilter((prev) => (prev === value ? "all" : value));
    };

    const filteredCustomers = useMemo(() => {

        return customers.filter(c => {

            const text = search.toLowerCase();

            const matchesSearch = (
                (c.firstname + " " + c.lastname).toLowerCase().includes(text) ||
                (c.username_org || "").toLowerCase().includes(text) ||
                (c.mobile || "").includes(text) ||
                (c.package_name || "").toLowerCase().includes(text) ||
                (c.branch_name || "").toLowerCase().includes(text)
            );

            if (!matchesSearch) return false;

            if (statusFilter === "active" && c.status !== "Active") return false;
            if (statusFilter === "expired" && c.status !== "Expired") return false;
            if (statusFilter === "new" && c.status !== "New") return false;
            if (statusFilter === "online" && !(c.status === "Active" && c.online_status === "ONLINE")) return false;
            if (statusFilter === "offline" && !(c.status === "Active" && c.online_status !== "ONLINE")) return false;

            if (partnerFilter !== "all" && getPartnerLabel(c) !== partnerFilter) return false;
            if (branchFilter !== "all" && c.branch_name !== branchFilter) return false;

            return true;

        });

    }, [customers, search, statusFilter, partnerFilter, branchFilter]);


    const redirectToCustomer = async (customer) => {

        try {

            const { data } = await apiClient.get(
                `/customer/get_customer_by_mobile.php?mobile=${customer.mobile}`
            );

            if (!data.success) {
                toast.error("Customer not found in Portal");
                return;
            }

            navigate(
                `/customer/${data.customer_id}?${deepLinkParam}=${customer.acc_id}`
            );

        } catch (err) {

            toast.error("Unable to open customer");

        }

    };

    return (

        <>

            <Header />

            <div className="internet-list-container">

                <div className="internet-list-header">

                    <h2>{providerLabel} Customers</h2>

                    <button
                        className="refresh-btn"
                        onClick={loadCustomers}
                    >
                        Refresh
                    </button>
                    <button
                        className="refresh-btn-mobile"
                        onClick={loadCustomers}
                    >
                        <HiOutlineRefresh />
                    </button>


                </div>

                {/* Stat cards double as the status filter — click one to
                    filter to it, click again (or "Total") to clear. */}
                <div className="internet-stat-cards">
                    <button
                        className={`internet-stat-card ${statusFilter === "all" ? "active" : ""}`}
                        onClick={() => setStatusFilter("all")}
                    >
                        <span className="internet-stat-count">{stats.total}</span>
                        <span className="internet-stat-label">Total</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-active ${statusFilter === "active" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("active")}
                    >
                        <span className="internet-stat-count">{stats.active}</span>
                        <span className="internet-stat-label">Active</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-expired ${statusFilter === "expired" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("expired")}
                    >
                        <span className="internet-stat-count">{stats.expired}</span>
                        <span className="internet-stat-label">Expired</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-new ${statusFilter === "new" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("new")}
                    >
                        <span className="internet-stat-count">{stats.new}</span>
                        <span className="internet-stat-label">New</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-online ${statusFilter === "online" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("online")}
                    >
                        <span className="internet-stat-count">{stats.online}</span>
                        <span className="internet-stat-label">Online</span>
                    </button>
                    <button
                        className={`internet-stat-card stat-offline ${statusFilter === "offline" ? "active" : ""}`}
                        onClick={() => toggleStatusFilter("offline")}
                    >
                        <span className="internet-stat-count">{stats.offline}</span>
                        <span className="internet-stat-label">Offline</span>
                    </button>
                    {stats.other > 0 && (
                        <div className="internet-stat-card stat-other">
                            <span className="internet-stat-count">{stats.other}</span>
                            <span className="internet-stat-label">Other</span>
                        </div>
                    )}
                </div>

                <div className="internet-list-filters">
                    <input
                        className="internet-search"
                        placeholder="Search Customer, Username, Mobile, Package..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    {/* Partner/branch filters — admin & superadmin only.
                        Operators are already restricted to a single branch
                        server-side, so this would only ever show one option
                        for them. */}
                    {(role === "admin" || role === "superadmin") && (
                        <>
                            <SearchableSelect
                                className="internet-filter-select"
                                options={partnerOptions}
                                value={partnerFilter}
                                onChange={setPartnerFilter}
                                allLabel="All Partners"
                            />

                            <SearchableSelect
                                className="internet-filter-select"
                                options={branchOptions}
                                value={branchFilter}
                                onChange={setBranchFilter}
                                allLabel="All Branches"
                            />
                        </>
                    )}
                </div>

                {loading ? (

                    <>
                        <div className="skeleton-loader">
                            {[...Array(6)].map((_, index) => (
                                <div key={index} className="skeleton-row">
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                    <div className="skeleton-cell"></div>
                                </div>
                            ))}
                        </div>
                    </>

                ) : (

                    <div className="operator-table-wrapper">

                        <table className="operator-table">

                            <thead>

                                <tr>

                                    <th>Username</th>
                                    <th>Status</th>
                                    <th>Mobile</th>
                                    <th>Name</th>
                                    <th>Expiry Date</th>
                                    <th>Package</th>
                                    <th>Sub Plan</th>

                                </tr>

                            </thead>

                            <tbody>

                                {filteredCustomers.length === 0 && (

                                    <tr>

                                        <td colSpan="7">

                                            No Customers Found

                                        </td>

                                    </tr>

                                )}

                                {filteredCustomers.map((c, index) => (

                                    <tr
                                        key={c.acc_id}
                                        onClick={() => setSelectedCustomer(c)}
                                        style={{ cursor: "pointer" }}
                                    >

                                        <td>
                                            <span
                                                className="internet-link"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    redirectToCustomer(c);
                                                }}
                                            >
                                                {c.username_org}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${c.status === "Active" ? c.online_status === "ONLINE" ? "active-green" : "active-gray" : "inactive"}`}>
                                                {c.status  === "Active" ? c.online_status === "ONLINE"
                                                    ? "ONLINE"
                                                    : "OFFLINE" : c.status}
                                            </span>
                                        </td>
                                        <td>
                                            {/* tel: opens the device's own dialer directly with the
                                                number pre-filled — no longer navigates to the
                                                customer profile, that's now the username's job. */}
                                            <a
                                                href={`tel:${c.mobile}`}
                                                className="internet-link"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {c.mobile}
                                            </a>
                                        </td>
                                        
                                        <td>

                                            {c.firstname} {c.lastname}

                                        </td>
                                        <td>{c.expiry_date}</td>
                                        <td>{c.package_name}</td>
                                        <td>{c.sub_plan_name}</td>
                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    </div>

                )}

            </div>

        </>
    );

};

export default InternetCustomerList;